import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { menuService, menuTypeService } from '../../utils/api';
import ConfirmationModal from '../ConfirmationModal';
import { useSchool } from '../../contexts/SchoolContext';

// Helper function to format dates
const formatDate = (dateString) => {
  console.log(`[CopyDayModal] formatDate called with input:`, dateString);
  
  // Parse the date parts to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(part => parseInt(part, 10));
  console.log(`[CopyDayModal] Parsed date parts:`, { year, month, day });
  
  // Create date with explicit year, month (0-based), and day
  // Set to noon to avoid timezone issues
  const date = new Date(year, month - 1, day, 12, 0, 0);
  console.log(`[CopyDayModal] Created Date object (with noon time):`, date.toString());
  
  // Compare with standard ISO string parsing
  const dateFromISOString = new Date(dateString);
  console.log(`[CopyDayModal] Date comparison:`, {
    ourMethod: date.toString(),
    standardMethod: dateFromISOString.toString(),
    dayDifference: date.getDate() - dateFromISOString.getDate()
  });
  
  // Try alternative date creation methods
  const dateTOption = new Date(`${dateString}T12:00:00`);
  console.log(`[CopyDayModal] With T12:00:00 added:`, dateTOption.toString());
  
  const options = { weekday: 'long', month: 'short', day: 'numeric' };
  const formatted = date.toLocaleDateString(undefined, options);
  console.log(`[CopyDayModal] Formatted date result:`, formatted);
  
  return formatted;
};

const CopyDayModal = ({ 
  isOpen, 
  onClose, 
  sourceDayData, // { dayNumber, date } for the source day
  sourceMenuId,
  sourceMenuTypeId,
  onCopyComplete 
}) => {
  console.log(`[CopyDayModal] Modal opened with sourceDayData:`, sourceDayData);
  // Log timezone information
  console.log(`[CopyDayModal] Browser timezone info:`, {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    offset: new Date().getTimezoneOffset() / -60, // Convert to hours with correct sign
    currentTime: new Date().toString()
  });
  
  const { t, i18n } = useTranslation();
  const { school } = useSchool();
  
  // State for form values
  const [targetMenuTypeId, setTargetMenuTypeId] = useState('');
  const [targetMonth, setTargetMonth] = useState('');
  const [selectedDays, setSelectedDays] = useState([]); // Changed to array for multi-select
  
  // State for data loading and UI
  const [menuTypes, setMenuTypes] = useState([]);
  const [availableDays, setAvailableDays] = useState([]);
  const [calendarWeeks, setCalendarWeeks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // State for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationData, setConfirmationData] = useState(null);
  
  // Get current date for default month value and reset state when modal opens
  useEffect(() => {
    if (!isOpen) return; // Only run when modal is opening
    
    console.log('[CopyDayModal] Modal opening - resetting all state');
    
    // Reset ALL state when modal opens
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    setTargetMonth(`${year}-${month}`);
    
    // Always reset to source menu type as default when opening
    setTargetMenuTypeId(sourceMenuTypeId || '');
    
    // Reset ALL modal state
    setSelectedDays([]);
    setError(null);
    setAvailableDays([]);
    setCalendarWeeks([]);
    setShowConfirmationModal(false);
    setConfirmationData(null);
    
    console.log('[CopyDayModal] State reset completed');
  }, [isOpen, sourceMenuTypeId]); // Removed targetMenuTypeId dependency to allow proper reset
  
  // Cleanup state when modal closes
  useEffect(() => {
    if (!isOpen) {
      console.log('[CopyDayModal] Modal closing - cleaning up state');
      // Reset all state to initial values when modal closes
      setTargetMenuTypeId('');
      setTargetMonth('');
      setSelectedDays([]);
      setAvailableDays([]);
      setCalendarWeeks([]);
      setLoading(false);
      setSubmitting(false);
      setError(null);
      setShowConfirmationModal(false);
      setConfirmationData(null);
    }
  }, [isOpen]);
  
  // Fetch menu types when the modal opens
  useEffect(() => {
    if (!isOpen || !school?.id) return;
    
    const fetchMenuTypes = async () => {
      try {
        setLoading(true);
        const menuTypesData = await menuTypeService.getMenuTypes(school.id, i18n.language || 'en');
        setMenuTypes(menuTypesData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching menu types:', err);
        setError(t('errors.failedToLoadMenuTypes'));
        setLoading(false);
      }
    };
    
    fetchMenuTypes();
  }, [isOpen, school?.id, t, i18n.language]);
  
  // Generate available days and calendar grid when target month changes
  useEffect(() => {
    if (!targetMonth) {
      setAvailableDays([]);
      setCalendarWeeks([]);
      return;
    }
    
    const [year, month] = targetMonth.split('-').map(n => parseInt(n, 10)); // month is 1-12
    const daysInMonth = new Date(year, month, 0).getDate();
    const days = [];
    
    // Generate all days in the month (only weekdays)
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month - 1, day);
      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      
      // Only include weekdays (Monday to Friday)
      if (dayOfWeek > 0 && dayOfWeek < 6) {
        // Construct the date string from local components to avoid timezone shifts
        const localDateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        
        days.push({
          dayNumber: day,
          date: localDateString,
          dayOfWeek: dayOfWeek // 1 = Monday, ..., 5 = Friday
        });
      }
    }
    
    setAvailableDays(days);
    
    // Create calendar grid with proper week organization
    const createCalendarGrid = () => {
      // Sort days by day number to ensure chronological order
      const sortedDays = [...days].sort((a, b) => a.dayNumber - b.dayNumber);
      
      // Create a proper weekly grid
      const weeks = [];
      let currentWeek = null;
      
      for (const day of sortedDays) {
        // If we're at the start of a week (Monday) or haven't started a week yet
        if (day.dayOfWeek === 1 || currentWeek === null) {
          if (currentWeek !== null) {
            // Push the completed week before starting a new one
            weeks.push(currentWeek);
          }
          // Start a new week with nulls in each position
          currentWeek = Array(5).fill(null);
        }
        
        // Place the day in the appropriate column (0-indexed)
        const columnIndex = day.dayOfWeek - 1; // Convert from 1-5 to 0-4
        currentWeek[columnIndex] = day;
      }
      
      // Don't forget to add the last week if it exists
      if (currentWeek !== null) {
        weeks.push(currentWeek);
      }
      
      return weeks;
    };
    
    setCalendarWeeks(createCalendarGrid());
    setSelectedDays([]); // Reset selected days when month changes
  }, [targetMonth]);
  
  // Handle day selection (toggle selection)
  const handleDayClick = (day) => {
    if (day) {
      const isSelected = selectedDays.some(d => d.date === day.date);
      
      if (isSelected) {
        // Remove day if already selected
        setSelectedDays(selectedDays.filter(d => d.date !== day.date));
      } else {
        // Add day to selection
        setSelectedDays([...selectedDays, day]);
      }
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedDays.length === 0) {
      toast.error(t('errors.selectDaysFirst', 'Please select at least one day first'));
      return;
    }
    
    try {
      console.log('[CopyDay] Starting form submission check');
      setSubmitting(true);
      
      // First check if a menu already exists for the target month and type
      const existingMenu = await menuService.checkExistingMenu(school?.id, targetMonth, targetMenuTypeId);
      
      // If a menu exists, show confirmation modal
      if (existingMenu) {
        console.log('[CopyDay] Found existing menu, showing confirmation modal:', existingMenu);
        setConfirmationData({
          targetMenuTypeId,
          targetMonth,
          targetDaysData: selectedDays // Now using array of days
        });
        setShowConfirmationModal(true);
        setSubmitting(false);
        return;
      }
      
      // If no existing menu, proceed with copy
      console.log('[CopyDay] No existing menu found, proceeding with copy');
      
      // Gather data for API request
      const data = {
        sourceSchoolId: school?.id,
        sourceMenuTypeId,
        sourceDayData,
        targetMenuTypeId,
        targetMonth,
        targetDaysData: selectedDays // Now sending array of days
      };
      
      console.log('[CopyDay] Submitting request with data:', JSON.stringify(data, null, 2));
      
      // Call the API
      console.log('[CopyDay] Calling menuService.copyDay()');
      const response = await menuService.copyDay(data);
      
      console.log('[CopyDay] API Response received:', response);
      console.log('[CopyDay] Response menuId:', response.menuId);
      console.log('[CopyDay] Response menu data structure:', response.menu ? {
        id: response.menu.id,
        days: response.menu.days ? `${response.menu.days.length} days` : 'No days',
        days_sample: response.menu.days ? response.menu.days.slice(0, 2) : 'N/A',
        day_structure: response.menu.days && response.menu.days.length > 0 ? 
          Object.keys(response.menu.days[0]) : 'N/A'
      } : 'No menu data');
      
      // Process the menu data, similar to copyWeek
      let processedMenuData = response.menu;
      
      if (!processedMenuData && Array.isArray(response.days)) {
        console.log('[CopyDay] Creating menu wrapper for days array');
        processedMenuData = {
          id: response.menuId,
          days: response.days
        };
      }
      
      if (!processedMenuData) {
        console.log('[CopyDay] Using entire response as menu data');
        processedMenuData = response;
      }
      
      console.log('[CopyDay] Final processed menu data to pass:', processedMenuData);
      onCopyComplete(response.menuId, processedMenuData);
      toast.success(t('copyDay.multiSuccess', 'Days copied successfully!'));
      onClose(); // Close modal on success
      
    } catch (error) {
      console.error('[CopyDay] Error:', error);
      toast.error(error.message || t('errors.failedToCopyDay', 'Failed to copy days'));
    } finally {
      setSubmitting(false);
    }
  };
  
  // Handle confirmation for overwriting existing menu
  const handleConfirmOverwrite = async () => {
    setShowConfirmationModal(false);
    if (!confirmationData || !confirmationData.targetDaysData || confirmationData.targetDaysData.length === 0) {
      toast.error(t('errors.missingConfirmationData', 'Missing confirmation data.'));
      return;
    }

    try {
      console.log('[CopyDay] Starting confirmed form submission');
      setSubmitting(true);

      const data = {
        sourceSchoolId: school?.id,
        sourceMenuTypeId, // from component props
        sourceDayData,    // from component props
        targetMenuTypeId: confirmationData.targetMenuTypeId,
        targetMonth: confirmationData.targetMonth,
        targetDaysData: confirmationData.targetDaysData // Now using array of days
      };

      console.log('[CopyDay] Submitting confirmed request with data:', JSON.stringify(data, null, 2));

      const response = await menuService.copyDay(data);

      console.log('[CopyDay] Confirmed API Response received:', response);

      let processedMenuData = response.menu;
      if (!processedMenuData && Array.isArray(response.days)) {
        processedMenuData = { id: response.menuId, days: response.days };
      }
      if (!processedMenuData) {
        processedMenuData = response;
      }

      onCopyComplete(response.menuId, processedMenuData);
      toast.success(t('copyDay.multiSuccess', 'Days copied successfully!'));
      onClose(); // Close modal on success

    } catch (error) {
      console.error('[CopyDay] Error in confirmed copy:', error);
      toast.error(error.message || t('errors.failedToCopyDay', 'Failed to copy days'));
    } finally {
      setSubmitting(false);
    }
  };
  
  // If the modal is not open, don't render anything
  if (!isOpen) return null;

  // Days of the week labels
  const weekdayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
  
  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-gray-500 bg-opacity-75 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium">{t('copyDay.title', 'Copy Day')}</h3>
          <button
            type="button"
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
            onClick={onClose}
          >
            <span className="sr-only">{t('common.close')}</span>
            &times;
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {error && (
            <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="mb-4 p-2 bg-gray-100 rounded text-sm">
            <p className="font-medium">{t('copyDay.sourceDay', 'Source day:')}</p>
            {console.log(`[CopyDayModal] Rendering source day. sourceDayData:`, sourceDayData)}
            {console.log(`[CopyDayModal] Source day date string:`, sourceDayData.date)}
            <div>
              {(() => {
                if (!sourceDayData) return '';
                
                // Extract year and month from date string
                const [year, month] = sourceDayData.date.split('-').map(p => parseInt(p, 10));
                
                // Create date using the explicit dayNumber
                const correctedDate = new Date(year, month - 1, sourceDayData.dayNumber, 12, 0, 0);
                
                // Format the corrected date
                const options = { weekday: 'long', month: 'short', day: 'numeric' };
                return correctedDate.toLocaleDateString(undefined, options);
              })()}
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('copyDay.targetMenuType', 'Target Menu Type')}
            </label>
            <select
              value={targetMenuTypeId}
              onChange={(e) => setTargetMenuTypeId(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              disabled={loading || submitting}
              required
            >
              <option value="">{t('copyDay.selectMenuType', 'Select Menu Type')}</option>
              {menuTypes.map((menuType) => (
                <option key={menuType.id} value={menuType.id}>
                  {menuType.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('copyDay.targetMonth', 'Target Month')}
            </label>
            <input
              type="month"
              value={targetMonth}
              onChange={(e) => setTargetMonth(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              disabled={loading || submitting}
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              {t('copyDay.targetDays', 'Target Days')} 
              <span className="ml-2 font-normal text-xs text-gray-500">
                ({t('copyDay.selectMultipleDays', 'Select one or more days')})
              </span>
            </label>
            
            {/* Calendar grid with 5-day rows (Monday-Friday) */}
            <div className="bg-gray-50 rounded p-2">
              {/* Day of week header row */}
              <div className="grid grid-cols-5 gap-2">
                {weekdayLabels.map((day, index) => (
                  <div key={`header-${index}`} className="text-center font-semibold text-sm text-gray-600 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Days grid - rendered in rows of 5 days with proper weekday alignment */}
              {calendarWeeks.map((week, weekIndex) => (
                <div key={`week-${weekIndex}`} className="grid grid-cols-5 gap-2 mt-2">
                  {week.map((day, dayIndex) => (
                    <div key={`cell-${weekIndex}-${dayIndex}`} className="text-center">
                      {day ? (
                        <button
                          type="button"
                          onClick={() => handleDayClick(day)}
                          className={`
                            w-full py-2 rounded focus:outline-none relative
                            ${selectedDays.some(d => d.date === day.date)
                              ? 'bg-blue-500 text-white font-bold' 
                              : 'bg-white hover:bg-gray-200 text-gray-800'}
                          `}
                          disabled={loading || submitting}
                        >
                          {day.dayNumber}
                          {selectedDays.some(d => d.date === day.date) && (
                            <span className="absolute top-0 right-0 h-3 w-3 bg-green-500 rounded-full"></span>
                          )}
                        </button>
                      ) : (
                        <div className="py-2 opacity-0">-</div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          
          {selectedDays.length > 0 && (
            <div className="mb-4 p-2 bg-gray-100 rounded text-sm">
              <p className="font-medium">
                {t('copyDay.selectedDays', 'Selected days:')} {selectedDays.length}
              </p>
              <div className="max-h-32 overflow-y-auto mt-1">
                <ul className="list-disc pl-5">
                  {selectedDays
                    .sort((a, b) => a.dayNumber - b.dayNumber)
                    .map(day => (
                      <li key={day.date}>{formatDate(day.date)}</li>
                    ))}
                </ul>
              </div>
            </div>
          )}
          
          <div className="flex justify-end pt-2">
            <button
              type="button"
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2"
              onClick={onClose}
              disabled={loading || submitting}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              disabled={loading || submitting || !targetMenuTypeId || !targetMonth || selectedDays.length === 0}
            >
              {loading || submitting ? t('common.loading') : t('copyDay.copy', 'Copy')}
            </button>
          </div>
        </form>
      </div>
      
      {/* Confirmation Modal for Overwriting Existing Menu */}
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirmOverwrite}
        title={t('copyDay.confirmOverwrite', 'Confirm Overwrite')}
        message={t('copyDay.confirmOverwriteMultipleMessage', 'A menu already exists for the target month and menu type. Do you want to overwrite the menu items for the {{count}} selected days?', { count: confirmationData?.targetDaysData?.length || 0 })}
      />
    </div>
  );
};

export default CopyDayModal; 